import React, { useState } from "react";
import { Dispatch, SetStateAction } from "react";
import { WarningTwoTone } from "@ant-design/icons";
import type { NotificationArgsProps } from "antd";
import { notification } from "antd";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useConfigEnv from "../../../zustand";

type NotificationPlacement = NotificationArgsProps["placement"];

const Search = ({
  school,
  inputValue,
  setInputValue,
  captchaToken,
  setCaptchaToken,
}: {
  school: string;
  inputValue: string;
  setInputValue: Dispatch<SetStateAction<string>>;
  captchaToken: string;
  setCaptchaToken: Dispatch<SetStateAction<string>>;
}) => {
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCaptchaVisible, setIsCaptchaVisible] = useState(false);
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const { short_name } = useParams(); // Capture "nonglam"

  const [api, contextHolder] = notification.useNotification({
    bottom: 50,
  });

  const openNotification = (
    placement: NotificationPlacement,
    message: string,
    description: string
  ) => {
    api.info({
      message: message,
      description: description,
      icon: <WarningTwoTone twoToneColor="red" />,

      placement,
    });
  };

  const handleTransitionEnd = () => {
    if (isActive) {
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  };

  const onStudentClick = () => {
    setIsActive(false);
  };

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };
  const { env }: any = useConfigEnv();

  const fetchData = async (studentId: string) => {
    try {
      const response = await fetch(`${env?.apiCall}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "res.partner",
          method: "web_search_read",
          ids: [],
          kwargs: {
            specification: {
              stud_id: {},
              tmp_stud_id: {},
            },
            offset: 0,
            order: "",
            limit: 1,
            domain: [
              "|",
              ["stud_id", "=", studentId],
              ["tmp_stud_id", "=", studentId],
            ],
          },
        }),
      });

      if (response.status === 200) {
        const res = await response.json();
        const tmpStudId = res?.records?.[0]?.tmp_stud_id;
        const studId = res?.records?.[0]?.stud_id;

        if (res?.records?.length > 0 && short_name) {
          window.location.href = `/payment/detail/${short_name}?studentId=${studId}&applicationID=${tmpStudId}&lang=${language}`;
        } else if (res?.records?.length > 0 && !short_name) {
          window.location.href = `/payment/detail?studentId=${studId}&applicationID=${tmpStudId}&lang=${language}`;
        } else {
          openNotification(
            "bottomRight",
            "Không tìm thấy",
            "Không có dữ liệu cho mã sinh viên này"
          );
        }
      } else {
        openNotification("bottomRight", "Lỗi", "Không thể kết nối với server");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      openNotification(
        "bottomRight",
        "Lỗi",
        "Có lỗi xảy ra trong quá trình lấy dữ liệu"
      );
    }
  };

  const handleSearch = () => {
    if (inputValue.length === 0) {
      openNotification("bottomRight", t("missing_std_code"), t("error_lookup"));
    } else {
      setIsCaptchaVisible(true);
      fetchData(inputValue.trim());
    }
  };

  const handleCaptchaChange = (token: string | null) => {
    if (token) {
      setCaptchaToken(token);
      fetchData(inputValue.trim());
    }
    setIsCaptchaVisible(false);
  };

  return (
    <>
      {contextHolder}

      <div className="flex flex-col items-center justify-center gap-4">
        <div className="flex h-[64px] w-full flex-col items-start md:h-full">
          <div
            className={`${
              isActive === null ? "bg-white" : "bg-[#FAFAFA]"
            } flex h-[64px] rounded-[99px] border-[1px] border-[#F0F0F0] tracking-wider shadow-[0_12px_16px_0_rgba(0,0,0,0.05)] md:h-full md:w-full`}
          >
            {/* <div
              className={` flex flex-col justify-center gap-[2px] rounded-[99px] px-4 py-[14px] md:w-[240px] md:px-8`}
            >
              <p className='text-sm font-medium text-[#2D2D2D]'>Cơ sở</p>
              <p className={`text-left text-sm  font-semibold !text-[#2D2D2D]`}>
                Tra cứu thông tin
              </p>
            </div> */}
            {isActive ?? (
              <></>
              // <div className="my-4 w-[1px] flex-[none] bg-[#F0F0F0]"></div>
            )}
            <div
              className={`${
                isActive === false
                  ? "bg-white shadow-active-1"
                  : "bg-tranparent"
              } flex cursor-pointer items-center justify-between rounded-[99px] py-[8px] pl-8 pr-[8px]`}
              onClick={onStudentClick}
            >
              <div className="w-[250px] py-[6px] md:w-[510px] flex flex-col items-start">
                <p className="text-normal font-medium text-[#2D2D2D] text-left">
                  {t("lookup1")}
                </p>
                <input
                  className={`${
                    isActive === true || (isActive === null && "cursor-pointer")
                  } w-[90%] bg-transparent text-base font-medium text-[#2D2D2D] placeholder:text-sm placeholder:font-normal placeholder:text-[#A4A4A4] focus:outline-none focus-visible:outline-none md:w-full`}
                  type="text"
                  placeholder={t("enter_student_code")}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              </div>
              <button
                className={`relative flex h-[50px] items-center justify-center rounded-full bg-primary p-[13px] transition-all duration-300 ${
                  isActive === false ? "w-[50px] md:w-[160px]" : "w-[50px]"
                }`}
                onTransitionEnd={handleTransitionEnd}
                onClick={() => {
                  if (isActive === false) handleSearch();
                }}
              >
                <div className="absolute left-3 h-6 w-6">
                  <img
                    src="/images/payment/search-white.svg"
                    alt="search"
                    width={24}
                    height={24}
                  />
                </div>
                <p
                  className={`text-sm font-normal text-transparent transition-all md:text-base ${
                    isActive === false
                      ? "duration-0 md:duration-100"
                      : "duration-0"
                  } ${
                    isExpanded && isActive === false
                      ? "md:ml-5 md:!text-[#FFF] md:opacity-100"
                      : "md:h-0 md:w-0 md:opacity-0"
                  }`}
                >
                  {t("search")}
                </p>
              </button>
            </div>
          </div>
        </div>
        {/* {isCaptchaVisible && (
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
            onChange={handleCaptchaChange}
          />
        )} */}
        {/* <button onClick={handleCaptchaChange}>a</button> */}
      </div>
    </>
  );
};

export default Search;
